import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  setToken, removeToken,
  getUser as getStoredUser, setUser as setStoredUser, removeUser as removeStoredUser,
} from '../utils/tenantStorage';
import { disablePush } from '../utils/pushClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());

  const login = useCallback((userData, token) => {
    // Derive slug from userData so the key is always correct for the tenant
    const slug = userData.tenantSlug || undefined;
    setToken(token, slug);
    setStoredUser(userData, slug);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    // Unsubscribe this device from push BEFORE clearing the token (the server
    // DELETE needs auth), so a shared/kiosk device stops receiving the
    // previous user's notifications. Best-effort with a timeout — never block
    // logout on it, and swallow any failure.
    try {
      await Promise.race([
        disablePush(),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    } catch {
      /* push cleanup is best-effort */
    }
    // Clear only THIS tenant's session — other tenant tabs keep their own keys
    removeToken();
    removeStoredUser();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      setStoredUser(updated, updated.tenantSlug || undefined);
      return updated;
    });
  }, []);

  // Check if the current user has a specific permission (works for ALL roles)
  const hasPermission = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'program_admin') return true;
    return user.permissions?.[key] === true;
  }, [user]);

  // Derived role helpers (backward compatible — derived from permissions where possible)
  const isAdmin = user?.role === 'program_admin' || user?.permissions?.can_manage_users === true;
  const isProgramAdmin = user?.role === 'program_admin';
  const isManager = user?.role === 'manager' || (user?.permissions && Object.values(user.permissions).some(v => v === true) && !isAdmin);
  const isPayrollAdmin = user?.permissions?.can_run_payroll === true && !isAdmin;
  const isAdminOrManager = isAdmin || isManager;
  const hasPayrollAccess = user?.permissions?.can_run_payroll === true || isProgramAdmin;

  const value = useMemo(() => ({
    user,
    login,
    logout,
    updateUser,
    hasPermission,
    isAdmin,
    isPayrollAdmin,
    isManager,
    isProgramAdmin,
    isAdminOrManager,
    hasPayrollAccess,
  }), [user, login, logout, updateUser, hasPermission, isAdmin, isPayrollAdmin, isManager, isProgramAdmin, isAdminOrManager, hasPayrollAccess]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
